import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ProgressBar from "../../questionnaire/components/ProgressBar";
import AppButton from "../../questionnaire/components/AppButton";
import { useAppointmentSteps, useAppointmentStepPath } from "../../questionnaire/routes/stepConfig";
import { useWorkflow } from "../../questionnaire/contexts/WorkflowContext";
import { searchAllergies, AllergySearchItem, searchMedications, MedicationSearchItem } from "../api/athena";
import { Alert } from "../../components/shared/Alert";
import axiosInstance from "../../utils/axiosConfig";

interface FamilyHistoryInstance {
  default: string;
  relation: string;
  relationkeyid: string;
  onsetage?: string;
  diedofage?: string;
}

interface HealthHistoryQuestion {
  portalformquestionid: string;
  text: string;
  fields: {
    [key: string]: string | string[] | FamilyHistoryInstance[];
  } | FamilyHistoryInstance[];
  pastmedicalhistoryquestionid?: string;
  surgicalhistoryprocedureid?: string;
  procedurecode?: string;
  cvx?: string;
  inputtype?: string;
  dropdownvalues?: string[][];
  clinicalelementid?: string;
  localclinicalelementid?: string;
  ordering?: string;
  snomedcode?: string;
  snomedfamilyhistoryproblemid?: string;
  origtext?: string;
  // Medication fields (stored directly on question, not in fields)
  medicationid?: string | number;
  rxnormid?: string | number;
  rxnormtype?: string;
  rxnormname?: string;
  name?: string;
  startdate?: string;
  stopdate?: string;
  meddeactivatedreasonid?: string;
  frequency?: string;
  displaydosageunits?: string;
  dosagequantity?: string | number;
  sig?: string;
  externalnote?: string;
}

interface HealthHistorySection {
  portalformsectionid: string | number;
  type: string;
  name: string;
  complete: boolean;
  empty: boolean;
  questions: HealthHistoryQuestion[];
  fielddata?: {
    default?: {
      inputtype: string;
    };
    surgerydate?: {
      inputtype: string;
      list?: number;
    };
    administered?: {
      inputtype: string;
      required?: number;
      list?: number;
    };
    relation?: {
      inputtype: string;
      required?: number;
      dropdownvalues?: string[][];
    };
    onsetage?: {
      inputtype: string;
    };
    diedofage?: {
      inputtype: string;
    };
    allergyreactionid?: {
      inputtype: string;
      dropdownvalues?: string[][];
      list?: number;
    };
    severityid?: {
      inputtype: string;
      dropdownvalues?: Array<{
        id: string;
        snomedcode?: string;
        description: string;
        ordering?: string;
        parentcode?: string;
      }>;
      list?: number;
    };
  };
  ageunit?: string;
  minage?: number;
  populated?: string;
  populatedfrom?: string;
}

interface FormSection {
  portalformsectionid: string | number;
  type: string;
  name: string;
  questions: HealthHistoryQuestion[];
}

// Reactions and severities are now loaded from backend fielddata

// Helper function to convert date from YYYY-MM-DD (date input) to MM/DD/YYYY (backend format)
const formatDateForBackend = (dateString: string): string => {
  if (!dateString) return "";
  // If already in MM/DD/YYYY format, return as is
  if (dateString.includes("/")) {
    return dateString;
  }
  // Convert from YYYY-MM-DD to MM/DD/YYYY
  const parts = dateString.split("-");
  if (parts.length === 3) {
    return `${parts[1]}/${parts[2]}/${parts[0]}`;
  }
  return dateString;
};

// Helper function to convert date from MM/DD/YYYY (backend) to YYYY-MM-DD (date input format)
const formatDateForInput = (dateString: string): string => {
  if (!dateString) return "";
  // If already in YYYY-MM-DD format, return as is
  if (dateString.includes("-") && dateString.length === 10) {
    return dateString;
  }
  // Convert from MM/DD/YYYY to YYYY-MM-DD
  const parts = dateString.split("/");
  if (parts.length === 3) {
    const month = parts[0].padStart(2, "0");
    const day = parts[1].padStart(2, "0");
    const year = parts[2];
    return `${year}-${month}-${day}`;
  }
  return dateString;
};

// Helper function to safely get default value from fields
const getFieldDefault = (fields: HealthHistoryQuestion["fields"]): string => {
  if (Array.isArray(fields)) {
    return ""; // FAMILY sections use array, not default property
  }
  if (fields && typeof fields === "object") {
    const defaultVal = (fields as any).default;
    return typeof defaultVal === "string" ? defaultVal : "";
  }
  return "";
};

// Helper function to render custom question inputs
const renderCustomQuestion = (question: HealthHistoryQuestion, sectionType: string, onChange?: () => void) => {
  const inputName = `${sectionType.toLowerCase()}_${question.portalformquestionid}`;
  const defaultValue = getFieldDefault(question.fields);
  const inputType = question.inputtype || "FREETEXT";

  switch (inputType) {
    case "YESNO":
      const isYes = defaultValue === "YES" || defaultValue === "Y" || defaultValue === "yes";
      const isNo = defaultValue === "NO" || defaultValue === "N" || defaultValue === "no";
      return (
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm text-[#1C1C1C]">
            <input
              type="radio"
              name={inputName}
              value="YES"
              defaultChecked={isYes}
              onChange={onChange}
              className="w-4 h-4 border border-[#BEC5D2] accent-[#01545D]"
            />
            <span>Yes</span>
          </label>
          <label className="flex items-center gap-2 text-sm text-[#1C1C1C]">
            <input
              type="radio"
              name={inputName}
              value="NO"
              defaultChecked={isNo && !isYes}
              onChange={onChange}
              className="w-4 h-4 border border-[#BEC5D2] accent-[#01545D]"
            />
            <span>No</span>
          </label>
        </div>
      );

    case "DROPDOWN":
      return (
        <select
          name={inputName}
          className="w-full md:w-96 rounded-md border border-[#DCE1E8] py-3 px-3 text-sm text-[#1C1C1C] bg-white focus:border-[#01545D]"
          defaultValue={defaultValue}
          onChange={onChange}
        >
          <option value="">Select an option</option>
          {question.dropdownvalues?.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      );

    case "DATE":
      return (
        <input
          type="date"
          name={inputName}
          defaultValue={defaultValue ? formatDateForInput(defaultValue) : ""}
          className="w-full md:w-48 rounded-md border border-[#DCE1E8] py-3 px-3 text-sm text-[#1C1C1C] placeholder-[#818BA0] focus:border-[#01545D]"
        />
      );

    case "FREETEXT":
    default:
      return (
        <input
          type="text"
          name={inputName}
          placeholder="Enter your answer"
          defaultValue={defaultValue}
          onChange={onChange}
          className="w-full rounded-md border border-[#DCE1E8] py-3 px-3 text-sm text-[#1C1C1C] placeholder-[#818BA0] focus:border-[#01545D]"
        />
      );
  }
};

const MedicalHistory: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { getNextStepInSequence, getPreviousStep, isAllStepsCompleted, error: workflowError } = useWorkflow();
  const steps = useAppointmentSteps("medical-history");
  const carePath = useAppointmentStepPath("care-and-insurance");
  const wellnessPath = useAppointmentStepPath("wellness-check");

  const [loading, setLoading] = useState(true);
  const [isFormAlreadySubmitted, setIsFormAlreadySubmitted] = useState(false);
  const [sectionsByType, setSectionsByType] = useState<Record<string, HealthHistorySection | null>>({
    MEDICAL: null,
    SURGICAL: null,
    VACCINATION: null,
    SOCIAL: null,
    OBGYN: null,
    FAMILY: null,
    ALLERGY: null,
    MEDICATION: null,
  });

  useEffect(() => {
    const fetchFormContent = async () => {
      if (!appointmentId) return;

      try {
        const response = await axiosInstance.get(
          `/api/athena/digital-checkin/appointment/${encodeURIComponent(appointmentId)}/health-history-form-content`,
          { headers: { client: 'axis', domain: 'axis' } }
        );

        const sections: HealthHistorySection[] = response.data;

        const sectionsMap: Record<string, HealthHistorySection | null> = {
          MEDICAL: null,
          SURGICAL: null,
          VACCINATION: null,
          SOCIAL: null,
          OBGYN: null,
          FAMILY: null,
          ALLERGY: null,
          MEDICATION: null,
        };

        sections.forEach((section) => {
          const sectionType = section.type.toUpperCase();
          if (sectionsMap.hasOwnProperty(sectionType)) {
            sectionsMap[sectionType] = section;
          }
        });

        setSectionsByType(sectionsMap);
        setIsFormAlreadySubmitted(false);
      } catch (error: any) {
        console.error("Failed to fetch health history form content:", error);
        
        // Check if error is 409 with "Health history form step not found" message
        const statusCode = error?.response?.status;
        const errorData = error?.response?.data;
        
        if (statusCode === 409) {
          // Check if error message indicates form is already submitted
          const errorMessage = Array.isArray(errorData) 
            ? errorData.map((e: any) => e?.error || '').join(' ')
            : (typeof errorData === 'string' ? errorData : JSON.stringify(errorData || ''));
          
          if (errorMessage.toLowerCase().includes('health history form step not found')) {
            setIsFormAlreadySubmitted(true);
            setLocalError(null); // Clear any previous errors
          } else {
            setLocalError("Unable to load form content. Please try again.");
          }
        } else {
          setLocalError("Unable to load form content. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchFormContent();
  }, [appointmentId]);

  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  // Track which sections the user has interacted with
  const [touchedSections, setTouchedSections] = useState<Set<string>>(new Set());
  // Track instances per problem: { [problemId]: [instanceId1, instanceId2, ...] }
  const [familyInstances, setFamilyInstances] = useState<Record<string, number[]>>({});
  // Track allergies: { [allergyId]: { allergenid, name, onsetdate, reactions: [{ reactionId, severityId }] } }
  const [allergies, setAllergies] = useState<Record<number, {
    allergenid?: string | number;
    name?: string;
    onsetdate?: string;
    reactions: Array<{ reactionId: string; severityId: string }>;
  }>>({});
  // Track reaction instances per allergy: { [allergyId]: [reactionInstanceId1, ...] }
  const [allergyReactions, setAllergyReactions] = useState<Record<number, number[]>>({});
  // Track medications: { [medicationId]: { medicationid, rxnormid, rxnormtype, rxnormname, name, startdate, stopdate, meddeactivatedreasonid, externalnote } }
  const [medications, setMedications] = useState<Record<number, {
    medicationid?: string | number;
    rxnormid?: string | number;
    rxnormtype?: string;
    rxnormname?: string;
    name?: string;
    startdate?: string;
    stopdate?: string;
    meddeactivatedreasonid?: string;
    externalnote?: string;
  }>>({});
  
  // Medication search state
  const [medicationSearchResults, setMedicationSearchResults] = useState<Record<number, MedicationSearchItem[]>>({});
  const [medicationSearchLoading, setMedicationSearchLoading] = useState<Record<number, boolean>>({});
  const [medicationSearchValue, setMedicationSearchValue] = useState<Record<number, string>>({});
  
  // Allergy search state
  const [allergySearchResults, setAllergySearchResults] = useState<Record<number, AllergySearchItem[]>>({});
  const [allergySearchLoading, setAllergySearchLoading] = useState<Record<number, boolean>>({});
  const [allergySearchValue, setAllergySearchValue] = useState<Record<number, string>>({});

  // Track relationkeyid counters per relation type
  const [relationKeyCounters, setRelationKeyCounters] = useState<Record<string, number>>({});

  // Initialize family instances from existing data
  React.useEffect(() => {
    if (sectionsByType.FAMILY) {
      const instances: Record<string, number[]> = {};
      const counters: Record<string, number> = {};
      
      sectionsByType.FAMILY.questions.forEach((question) => {
        // For FAMILY sections, fields is directly an array of instances
        const existingFields = Array.isArray(question.fields) ? question.fields : [];
        if (existingFields.length > 0) {
          const instanceIds: number[] = [];
          existingFields.forEach((instance: any, idx: number) => {
            const instanceId = Date.now() + idx + Math.random();
            instanceIds.push(instanceId);
            
            // Track relationkeyid counters
            const relation = instance.relation || "";
            if (relation && instance.relationkeyid) {
              const keyId = parseInt(instance.relationkeyid, 10);
              if (!counters[relation] || counters[relation] < keyId) {
                counters[relation] = keyId;
              }
            }
          });
          if (instanceIds.length > 0) {
            instances[question.portalformquestionid] = instanceIds;
          }
        }
      });
      
      setFamilyInstances(instances);
      setRelationKeyCounters(counters);
    }
  }, [sectionsByType.FAMILY]);

  const addFamilyInstance = (problemId: string) => {
    markSectionTouched('FAMILY');
    setFamilyInstances((prev) => ({
      ...prev,
      [problemId]: [...(prev[problemId] || []), Date.now() + Math.random()],
    }));
  };

  const removeFamilyInstance = (problemId: string, instanceId: number) => {
    setFamilyInstances((prev) => ({
      ...prev,
      [problemId]: (prev[problemId] || []).filter((id) => id !== instanceId),
    }));
  };

  const getNextRelationKeyId = (relation: string): string => {
    setRelationKeyCounters((prev) => {
      const current = prev[relation] || 0;
      return { ...prev, [relation]: current + 1 };
    });
    const current = relationKeyCounters[relation] || 0;
    return String(current + 1);
  };

  // Initialize allergies from existing data
  React.useEffect(() => {
    if (sectionsByType.ALLERGY) {
      const allergyData: Record<number, {
        allergenid?: string | number;
        name?: string;
        onsetdate?: string;
        reactions: Array<{ reactionId: string; severityId: string }>;
      }> = {};
      const reactionData: Record<number, number[]> = {};
      
      sectionsByType.ALLERGY.questions.forEach((question, idx) => {
        const allergyId = Date.now() + idx + Math.random();
        
        // Backend returns data directly on question object (allergenid, allergyid, name, onsetdate, allergyreactionid, severityid)
        // But also support old format where data is in fields
        const questionAny = question as any;
        const allergenid = questionAny.allergenid || questionAny.allergyid || (Array.isArray(question.fields) ? undefined : (question.fields as any)?.allergenid);
        const name = questionAny.name || question.text || (Array.isArray(question.fields) ? undefined : (question.fields as any)?.name);
        const onsetdate = questionAny.onsetdate || (Array.isArray(question.fields) ? undefined : (question.fields as any)?.onsetdate);
        const allergyreactionid = questionAny.allergyreactionid || (Array.isArray(question.fields) ? undefined : (question.fields as any)?.allergyreactionid);
        const severityid = questionAny.severityid || (Array.isArray(question.fields) ? undefined : (question.fields as any)?.severityid);
        
        const reactions: Array<{ reactionId: string; severityId: string }> = [];
        const reactionIds: number[] = [];
        
        // Handle reactions and severities as arrays
        if (Array.isArray(allergyreactionid) && Array.isArray(severityid)) {
          allergyreactionid.forEach((reactionId: string | number, reactionIdx: number) => {
            const reactionInstanceId = Date.now() + idx + reactionIdx + Math.random();
            reactionIds.push(reactionInstanceId);
            reactions.push({
              reactionId: String(reactionId),
              severityId: String(severityid[reactionIdx] || ""),
            });
          });
        } else if (allergyreactionid && severityid) {
          // Handle single reaction/severity
          const reactionInstanceId = Date.now() + idx + Math.random();
          reactionIds.push(reactionInstanceId);
          reactions.push({
            reactionId: String(allergyreactionid),
            severityId: String(severityid),
          });
        }
        
        allergyData[allergyId] = {
          allergenid,
          name,
          onsetdate,
          reactions,
        };
        reactionData[allergyId] = reactionIds;
      });
      
      setAllergies(allergyData);
      setAllergyReactions(reactionData);
    }
  }, [sectionsByType.ALLERGY]);

  const addAllergy = () => {
    markSectionTouched('ALLERGY');
    const newId = Date.now() + Math.random();
    setAllergies((prev) => ({
      ...prev,
      [newId]: { reactions: [] },
    }));
    setAllergyReactions((prev) => ({
      ...prev,
      [newId]: [],
    }));
  };

  const removeAllergy = (id: number) => {
    setAllergies((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
    setAllergyReactions((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
    setAllergySearchResults((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
    setAllergySearchValue((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const addAllergyReaction = (allergyId: number) => {
    markSectionTouched('ALLERGY');
    setAllergyReactions((prev) => ({
      ...prev,
      [allergyId]: [...(prev[allergyId] || []), Date.now() + Math.random()],
    }));
  };

  const removeAllergyReaction = (allergyId: number, reactionId: number) => {
    setAllergyReactions((prev) => ({
      ...prev,
      [allergyId]: (prev[allergyId] || []).filter((id) => id !== reactionId),
    }));
  };

  // Debounce timers for allergy search
  const allergySearchTimers = React.useRef<Record<number, NodeJS.Timeout>>({});

  const handleAllergySearch = async (allergyId: number, searchValue: string) => {
    const trimmedValue = searchValue.trim();
    
    if (!trimmedValue) {
      setAllergySearchResults((prev) => ({ ...prev, [allergyId]: [] }));
      return;
    }

    // Only search if at least 3 characters are typed
    if (trimmedValue.length < 3) {
      setAllergySearchResults((prev) => ({ ...prev, [allergyId]: [] }));
      return;
    }

    setAllergySearchLoading((prev) => ({ ...prev, [allergyId]: true }));
    
    try {
      const result = await searchAllergies(trimmedValue);
      setAllergySearchResults((prev) => ({
        ...prev,
        [allergyId]: result || [],
      }));
    } catch (error) {
      console.error("Failed to search allergies", error);
      setAllergySearchResults((prev) => ({ ...prev, [allergyId]: [] }));
    } finally {
      setAllergySearchLoading((prev) => ({ ...prev, [allergyId]: false }));
    }
  };

  const handleAllergySearchChange = (allergyId: number, value: string) => {
    markSectionTouched('ALLERGY');
    setAllergySearchValue((prev) => ({ ...prev, [allergyId]: value }));
    
    // Clear existing timer
    if (allergySearchTimers.current[allergyId]) {
      clearTimeout(allergySearchTimers.current[allergyId]);
    }
    
    // Set new timer
    allergySearchTimers.current[allergyId] = setTimeout(() => {
      handleAllergySearch(allergyId, value);
    }, 300);
  };

  const handleSelectAllergy = (allergyId: number, allergy: AllergySearchItem) => {
    setAllergies((prev) => ({
      ...prev,
      [allergyId]: {
        ...prev[allergyId],
        allergenid: allergy.allergenid,
        name: allergy.allergenname || allergy.allergy || "",
      },
    }));
    setAllergySearchResults((prev) => ({ ...prev, [allergyId]: [] }));
    setAllergySearchValue((prev) => ({ ...prev, [allergyId]: "" }));
  };

  // Initialize medications from existing data
  React.useEffect(() => {
    if (sectionsByType.MEDICATION) {
      const medicationData: Record<number, {
        medicationid?: string | number;
        rxnormid?: string | number;
        rxnormtype?: string;
        rxnormname?: string;
        name?: string;
        startdate?: string;
        stopdate?: string;
        meddeactivatedreasonid?: string;
        externalnote?: string;
      }> = {};
      
      sectionsByType.MEDICATION.questions.forEach((question, idx) => {
        const medicationId = Date.now() + idx + Math.random();
        medicationData[medicationId] = {
          medicationid: question.medicationid,
          rxnormid: question.rxnormid,
          rxnormtype: question.rxnormtype,
          rxnormname: question.rxnormname,
          name: question.name || question.text,
          startdate: question.startdate,
          stopdate: question.stopdate,
          meddeactivatedreasonid: question.meddeactivatedreasonid,
          externalnote: question.externalnote,
        };
      });
      
      setMedications(medicationData);
    }
  }, [sectionsByType.MEDICATION]);

  const addMedication = () => {
    markSectionTouched('MEDICATION');
    const newId = Date.now() + Math.random();
    setMedications((prev) => ({
      ...prev,
      [newId]: {},
    }));
  };

  const removeMedication = (id: number) => {
    setMedications((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
    setMedicationSearchResults((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
    setMedicationSearchValue((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  // Debounce timers for medication search
  const medicationSearchTimers = React.useRef<Record<number, NodeJS.Timeout>>({});

  const handleMedicationSearch = async (medicationId: number, searchValue: string) => {
    const trimmedValue = searchValue.trim();
    
    if (!trimmedValue) {
      setMedicationSearchResults((prev) => ({ ...prev, [medicationId]: [] }));
      return;
    }

    // Only search if at least 3 characters are typed
    if (trimmedValue.length < 3) {
      setMedicationSearchResults((prev) => ({ ...prev, [medicationId]: [] }));
      return;
    }

    setMedicationSearchLoading((prev) => ({ ...prev, [medicationId]: true }));
    
    try {
      const result = await searchMedications(trimmedValue);
      setMedicationSearchResults((prev) => ({
        ...prev,
        [medicationId]: result || [],
      }));
    } catch (error) {
      console.error("Failed to search medications", error);
      setMedicationSearchResults((prev) => ({ ...prev, [medicationId]: [] }));
    } finally {
      setMedicationSearchLoading((prev) => ({ ...prev, [medicationId]: false }));
    }
  };

  const handleMedicationSearchChange = (medicationId: number, value: string) => {
    markSectionTouched('MEDICATION');
    setMedicationSearchValue((prev) => ({ ...prev, [medicationId]: value }));
    
    // Clear existing timer
    if (medicationSearchTimers.current[medicationId]) {
      clearTimeout(medicationSearchTimers.current[medicationId]);
    }
    
    // Set new timer
    medicationSearchTimers.current[medicationId] = setTimeout(() => {
      handleMedicationSearch(medicationId, value);
    }, 300);
  };

  const handleSelectMedication = (medicationId: number, medication: MedicationSearchItem) => {
    setMedications((prev) => ({
      ...prev,
      [medicationId]: {
        ...prev[medicationId],
        medicationid: medication.medicationid,
        name: medication.medication,
      },
    }));
    setMedicationSearchResults((prev) => ({ ...prev, [medicationId]: [] }));
    setMedicationSearchValue((prev) => ({ ...prev, [medicationId]: "" }));
  };

  // Helper function to mark a section as touched
  const markSectionTouched = (sectionType: string) => {
    setTouchedSections((prev) => new Set(prev).add(sectionType));
  };

  // Helper function to check if any forms are visible
  const hasVisibleForms = () => {
    const sectionTypes = ['MEDICAL', 'SURGICAL', 'VACCINATION', 'SOCIAL', 'OBGYN', 'FAMILY', 'ALLERGY', 'MEDICATION'];
    return sectionTypes.some(type => {
      const section = sectionsByType[type];
      return section && section.complete !== true && (
        (type === 'ALLERGY' || type === 'MEDICATION') ? true : section.questions.length > 0
      );
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // If form is already submitted, just navigate to next step without calling API
    if (isFormAlreadySubmitted) {
      if (isAllStepsCompleted()) {
        navigate(`/appointments/${appointmentId}/complete`);
      } else {
        const nextStep = getNextStepInSequence(location.pathname);
        if (nextStep) {
          navigate(nextStep.stepUrl);
        } else {
          navigate(wellnessPath);
        }
      }
      return;
    }

    const formData = new FormData(e.currentTarget);
    const sections: HealthHistorySection[] = [];

    // MEDICAL Section - only include if touched
    if (sectionsByType.MEDICAL && touchedSections.has('MEDICAL')) {
      const medicalSection = sectionsByType.MEDICAL;
      const medicalQuestions: HealthHistoryQuestion[] = [];
      
      medicalSection.questions.forEach((question) => {
        const isChecked = formData.get(`medical_${question.portalformquestionid}`) === "Y";
        medicalQuestions.push({
          portalformquestionid: question.portalformquestionid,
          text: question.text,
          fields: {
            default: isChecked ? "Y" : "N",
          },
          pastmedicalhistoryquestionid: question.pastmedicalhistoryquestionid,
        });
      });

      sections.push({
        portalformsectionid: medicalSection.portalformsectionid,
        type: "MEDICAL",
        name: medicalSection.name,
        complete: true,
        empty: medicalQuestions.every((q) => {
          const fieldsObj = Array.isArray(q.fields) ? {} : (q.fields || {});
          return (fieldsObj as any)?.default === "N";
        }),
        questions: medicalQuestions,
      });
    }

    // SURGICAL Section - only include if touched
    if (sectionsByType.SURGICAL && touchedSections.has('SURGICAL')) {
      const surgicalSection = sectionsByType.SURGICAL;
      const surgicalQuestions: HealthHistoryQuestion[] = [];
      
      surgicalSection.questions.forEach((question) => {
        const isChecked = formData.get(`surgical_${question.portalformquestionid}_checked`) === "Y";
        const surgeryDate = formData.get(`surgical_${question.portalformquestionid}_date`) as string | null;
        
        const fields: { [key: string]: string | string[] } = {
          default: isChecked ? "Y" : "N",
        };
        
        // If checked and date is provided, add surgerydate as an array
        // Convert from YYYY-MM-DD (date input) to MM/DD/YYYY (backend format)
        if (isChecked && surgeryDate && surgeryDate.trim()) {
          fields.surgerydate = [formatDateForBackend(surgeryDate.trim())];
        }
        
        surgicalQuestions.push({
          portalformquestionid: question.portalformquestionid,
          text: question.text,
          fields,
          surgicalhistoryprocedureid: question.surgicalhistoryprocedureid,
          procedurecode: question.procedurecode,
        });
      });

      sections.push({
        portalformsectionid: surgicalSection.portalformsectionid,
        type: "SURGICAL",
        name: surgicalSection.name,
        complete: true,
        empty: surgicalQuestions.every((q) => {
          const fieldsObj = Array.isArray(q.fields) ? {} : (q.fields || {});
          return (fieldsObj as any)?.default === "N";
        }),
        questions: surgicalQuestions,
        ageunit: surgicalSection.ageunit,
        minage: surgicalSection.minage,
      });
    }

    // VACCINATION Section - only include if touched
    if (sectionsByType.VACCINATION && touchedSections.has('VACCINATION')) {
      const vaccinationSection = sectionsByType.VACCINATION;
      const vaccinationQuestions: HealthHistoryQuestion[] = [];
      
      vaccinationSection.questions.forEach((question) => {
        const isChecked = formData.get(`vaccination_${question.portalformquestionid}_checked`) === "Y";
        const administeredDate = formData.get(`vaccination_${question.portalformquestionid}_date`) as string | null;
        
        const fields: { [key: string]: string | string[] } = {
          default: isChecked ? "Y" : "N",
        };
        
        // If checked, administered date is required and must be submitted as an array
        // Convert from YYYY-MM-DD (date input) to MM/DD/YYYY (backend format)
        if (isChecked) {
          if (administeredDate && administeredDate.trim()) {
            fields.administered = [formatDateForBackend(administeredDate.trim())];
          } else {
            // If checked but no date, still include empty array or handle validation
            // Based on requirement, it's required, so we'll include it but validation should catch this
            fields.administered = [];
          }
        }
        
        vaccinationQuestions.push({
          portalformquestionid: question.portalformquestionid,
          text: question.text,
          fields,
          cvx: question.cvx,
        });
      });

      sections.push({
        portalformsectionid: vaccinationSection.portalformsectionid,
        type: "VACCINATION",
        name: vaccinationSection.name,
        complete: true,
        empty: vaccinationQuestions.every((q) => {
          const fieldsObj = Array.isArray(q.fields) ? {} : (q.fields || {});
          return (fieldsObj as any)?.default === "N";
        }),
        questions: vaccinationQuestions,
        ageunit: vaccinationSection.ageunit,
      });
    }

    // SOCIAL Section - only include if touched
    if (sectionsByType.SOCIAL && touchedSections.has('SOCIAL')) {
      const socialSection = sectionsByType.SOCIAL;
      const socialQuestions: HealthHistoryQuestion[] = [];
      
      socialSection.questions.forEach((question) => {
        let answer = formData.get(`social_${question.portalformquestionid}`) as string | null;
        
        // Convert YES/NO to Y/N for YESNO questions
        if (question.inputtype === "YESNO" && answer) {
          if (answer === "YES") {
            answer = "Y";
          } else if (answer === "NO") {
            answer = "N";
          }
        }
        
        socialQuestions.push({
          portalformquestionid: question.portalformquestionid,
          text: question.text,
          fields: {
            default: answer || "",
          },
          inputtype: question.inputtype,
          clinicalelementid: question.clinicalelementid,
          localclinicalelementid: question.localclinicalelementid,
          ordering: question.ordering,
        });
      });

      // Sort by ordering if present
      socialQuestions.sort((a, b) => {
        const orderA = a.ordering ? parseInt(a.ordering, 10) : 999;
        const orderB = b.ordering ? parseInt(b.ordering, 10) : 999;
        return orderA - orderB;
      });

      sections.push({
        portalformsectionid: socialSection.portalformsectionid,
        type: "SOCIAL",
        name: socialSection.name,
        complete: true,
        empty: socialQuestions.every((q) => {
          const fieldsObj = Array.isArray(q.fields) ? {} : (q.fields || {});
          const defaultVal = (fieldsObj as any)?.default;
          return !defaultVal || defaultVal === "";
        }),
        questions: socialQuestions,
        ageunit: socialSection.ageunit,
      });
    }

    // OBGYN Section - only include if touched
    if (sectionsByType.OBGYN && touchedSections.has('OBGYN')) {
      const obgynSection = sectionsByType.OBGYN;
      const obgynQuestions: HealthHistoryQuestion[] = [];
      
      obgynSection.questions.forEach((question) => {
        let answer = formData.get(`obgyn_${question.portalformquestionid}`) as string | null;
        
        // Convert YES/NO to Y/N for YESNO questions
        if (question.inputtype === "YESNO" && answer) {
          if (answer === "YES") {
            answer = "Y";
          } else if (answer === "NO") {
            answer = "N";
          }
        }
        
        obgynQuestions.push({
          portalformquestionid: question.portalformquestionid,
          text: question.text,
          fields: {
            default: answer || "",
          },
          inputtype: question.inputtype,
          clinicalelementid: question.clinicalelementid,
          localclinicalelementid: question.localclinicalelementid,
          ordering: question.ordering,
        });
      });

      // Sort by ordering if present
      obgynQuestions.sort((a, b) => {
        const orderA = a.ordering ? parseInt(a.ordering, 10) : 999;
        const orderB = b.ordering ? parseInt(b.ordering, 10) : 999;
        return orderA - orderB;
      });

      sections.push({
        portalformsectionid: obgynSection.portalformsectionid,
        type: "OBGYN",
        name: obgynSection.name,
        complete: true,
        empty: obgynQuestions.every((q) => {
          const fieldsObj = Array.isArray(q.fields) ? {} : (q.fields || {});
          const defaultVal = (fieldsObj as any)?.default;
          return !defaultVal || defaultVal === "";
        }),
        questions: obgynQuestions,
        ageunit: obgynSection.ageunit,
      });
    }

    // FAMILY Section - only include if touched
    if (sectionsByType.FAMILY && touchedSections.has('FAMILY')) {
      const familySection = sectionsByType.FAMILY;
      const familyQuestions: HealthHistoryQuestion[] = [];
      
      // Track relationkeyid counters per relation type across all problems
      const relationCounters: Record<string, number> = {};
      
      // Initialize counters from existing data
      familySection.questions.forEach((question) => {
        const existingFields = Array.isArray(question.fields) ? question.fields : [];
        existingFields.forEach((instance: any) => {
          const relation = instance.relation || "";
          if (relation && instance.relationkeyid) {
            const keyId = parseInt(instance.relationkeyid, 10);
            if (!relationCounters[relation] || relationCounters[relation] < keyId) {
              relationCounters[relation] = keyId;
            }
          }
        });
      });
      
      familySection.questions.forEach((question) => {
        const problemId = question.portalformquestionid;
        const instanceIds = familyInstances[problemId] || [];
        const instances: FamilyHistoryInstance[] = [];
        
        instanceIds.forEach((instanceId, idx) => {
          const defaultVal = formData.get(`family_${problemId}_${instanceId}_default`) as string | null;
          const relation = formData.get(`family_${problemId}_${instanceId}_relation`) as string | null;
          let relationKeyId = formData.get(`family_${problemId}_${instanceId}_relationkeyid`) as string | null;
          const onsetAge = formData.get(`family_${problemId}_${instanceId}_onsetage`) as string | null;
          const diedOfAge = formData.get(`family_${problemId}_${instanceId}_diedofage`) as string | null;
          
          // Generate relationkeyid if not provided (REQUIRED field)
          if (!relationKeyId && relation) {
            const currentCounter = relationCounters[relation] || 0;
            relationCounters[relation] = currentCounter + 1;
            relationKeyId = String(relationCounters[relation]);
          }
          
          // Only include if default is "Y" or if any field is filled
          if (defaultVal === "Y" || relation || onsetAge || diedOfAge) {
            instances.push({
              default: defaultVal === "Y" ? "Y" : "N",
              relation: relation || "",
              relationkeyid: relationKeyId || "1", // REQUIRED field
              onsetage: onsetAge || undefined,
              diedofage: diedOfAge || undefined,
            });
          }
        });
        
        // Only include question if it has instances
        if (instances.length > 0) {
          familyQuestions.push({
            portalformquestionid: question.portalformquestionid,
            text: question.text,
            fields: instances,
            snomedcode: question.snomedcode,
            snomedfamilyhistoryproblemid: question.snomedfamilyhistoryproblemid,
            origtext: question.origtext,
          });
        }
      });

      if (familyQuestions.length > 0) {
        sections.push({
          portalformsectionid: familySection.portalformsectionid,
          type: "FAMILY",
          name: familySection.name,
          complete: true,
          empty: false,
          questions: familyQuestions,
          ageunit: familySection.ageunit,
        });
      }
    }

    // ALLERGY Section - only include if touched
    if (sectionsByType.ALLERGY && touchedSections.has('ALLERGY')) {
      const allergySection = sectionsByType.ALLERGY;
      const allergyQuestions: any[] = [];
      
      Object.entries(allergies).forEach(([allergyIdStr, allergyData]) => {
        const allergyId = parseFloat(allergyIdStr);
        const allergenid = allergyData.allergenid;
        const name = formData.get(`allergy_${allergyId}_name`) as string | null || allergyData.name || "";
        const onsetdateRaw = formData.get(`allergy_${allergyId}_onsetdate`) as string | null || allergyData.onsetdate || "";
        // Convert from YYYY-MM-DD (date input) to MM/DD/YYYY (backend format)
        const onsetdate = onsetdateRaw ? formatDateForBackend(onsetdateRaw) : "";
        
        // Collect reactions and severities
        const reactionIds = allergyReactions[allergyId] || [];
        const allergyreactionid: number[] = [];
        const severityid: (number | string)[] = [];
        
        reactionIds.forEach((reactionInstanceId) => {
          const reactionId = formData.get(`allergy_${allergyId}_reaction_${reactionInstanceId}_id`) as string | null;
          const severityId = formData.get(`allergy_${allergyId}_reaction_${reactionInstanceId}_severity`) as string | null;
          
          if (reactionId) {
            // Convert reactionId to number (backend expects number)
            const reactionIdNum = parseInt(reactionId, 10);
            if (!isNaN(reactionIdNum)) {
              allergyreactionid.push(reactionIdNum);
              // Convert severityId to number if it's a numeric string, otherwise keep as string
              const severityIdNum = severityId && severityId !== "none" ? (isNaN(parseInt(severityId, 10)) ? severityId : parseInt(severityId, 10)) : undefined;
              if (severityIdNum !== undefined) {
                severityid.push(severityIdNum);
              }
            }
          }
        });
        
        // Only include if we have at least allergenid or name
        if (allergenid || name) {
          const allergyQuestion: any = {
            allergenid: allergenid ? (typeof allergenid === "string" ? parseInt(allergenid, 10) : allergenid) : undefined,
            allergyid: allergenid ? (typeof allergenid === "string" ? parseInt(allergenid, 10) : allergenid) : undefined,
            name: name || undefined,
            onsetdate: onsetdate || undefined,
          };
          
          if (allergyreactionid.length > 0) {
            allergyQuestion.allergyreactionid = allergyreactionid;
            allergyQuestion.severityid = severityid;
          }
          
          allergyQuestions.push(allergyQuestion);
        }
      });

      if (allergyQuestions.length > 0) {
        sections.push({
          portalformsectionid: allergySection.portalformsectionid,
          type: "ALLERGY",
          name: allergySection.name,
          complete: true,
          empty: false,
          questions: allergyQuestions,
        });
      }
    }

    // MEDICATION Section - only include if touched
    if (sectionsByType.MEDICATION && touchedSections.has('MEDICATION')) {
      const medicationSection = sectionsByType.MEDICATION;
      const medicationQuestions: HealthHistoryQuestion[] = [];
      
      Object.entries(medications).forEach(([medicationIdStr, medicationData]) => {
        const medicationId = parseFloat(medicationIdStr);
        const medicationid = formData.get(`medication_${medicationId}_medicationid`) as string | null || medicationData.medicationid;
        const rxnormid = formData.get(`medication_${medicationId}_rxnormid`) as string | null || medicationData.rxnormid;
        const rxnormtype = formData.get(`medication_${medicationId}_rxnormtype`) as string | null || medicationData.rxnormtype;
        const rxnormname = formData.get(`medication_${medicationId}_rxnormname`) as string | null || medicationData.rxnormname;
        const name = formData.get(`medication_${medicationId}_name`) as string | null || medicationData.name || "";
        const startdateRaw = formData.get(`medication_${medicationId}_startdate`) as string | null || medicationData.startdate;
        const stopdateRaw = formData.get(`medication_${medicationId}_stopdate`) as string | null || medicationData.stopdate;
        // Convert from YYYY-MM-DD (date input) to MM/DD/YYYY (backend format)
        const startdate = startdateRaw ? formatDateForBackend(startdateRaw) : undefined;
        const stopdate = stopdateRaw ? formatDateForBackend(stopdateRaw) : undefined;
        const meddeactivatedreasonid = formData.get(`medication_${medicationId}_meddeactivatedreasonid`) as string | null || medicationData.meddeactivatedreasonid;
        const externalnote = medicationData.externalnote; // Read-only, don't allow modification
        
        // Only include if we have at least medicationid
        if (medicationid) {
          // Medication questions don't have portalformquestionid, text, fields, or name
          // They only have medication-specific fields directly on the question object
          const medicationQuestion: any = {
            medicationid: typeof medicationid === "string" ? parseFloat(medicationid) : medicationid,
          };
          
          if (rxnormid) {
            medicationQuestion.rxnormid = typeof rxnormid === "string" ? parseFloat(rxnormid) : rxnormid;
          }
          if (rxnormtype) {
            medicationQuestion.rxnormtype = rxnormtype;
          }
          if (rxnormname) {
            medicationQuestion.rxnormname = rxnormname;
          }
          if (startdate) {
            medicationQuestion.startdate = startdate;
          }
          if (stopdate) {
            medicationQuestion.stopdate = stopdate;
          }
          if (meddeactivatedreasonid) {
            medicationQuestion.meddeactivatedreasonid = meddeactivatedreasonid;
          }
          
          medicationQuestions.push(medicationQuestion);
        }
      });

      if (medicationQuestions.length > 0) {
        sections.push({
          portalformsectionid: medicationSection.portalformsectionid,
          type: "MEDICATION",
          name: medicationSection.name,
          complete: true,
          empty: false,
          questions: medicationQuestions,
          ageunit: medicationSection.ageunit,
          minage: medicationSection.minage,
        });
      }
    }

    setSubmitting(true);
    setLocalError(null);
    try {
      // Wrap sections in formData array as per API specification
      const payload = {
        formData: sections,
      };

      // Call API even if no sections (empty array) - allows navigation
      console.log("payload", payload);

      await axiosInstance.put(`/api/athena/digital-checkin/appointment/${encodeURIComponent(String(appointmentId))}/health-history-forms`, payload, {
        headers: { client: 'axis', domain: 'axis' }
      });
 
      // Navigate to next step in sequence or completion
      if (isAllStepsCompleted()) {
        navigate(`/appointments/${appointmentId}/complete`);
      } else {
        const nextStep = getNextStepInSequence(location.pathname);
        if (nextStep) {
          navigate(nextStep.stepUrl);
        } else {
          navigate(wellnessPath);
        }
      }
    } catch (err) {
      setLocalError("Unable to save your information. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAndExit = async () => {
    // If form is already submitted, just navigate without calling API
    if (isFormAlreadySubmitted) {
      if (isAllStepsCompleted()) {
        navigate(`/appointments/${appointmentId}/complete`);
      } else {
        const nextStep = getNextStepInSequence(location.pathname);
        if (nextStep) {
          navigate(nextStep.stepUrl);
        } else {
          navigate(carePath.replace("care-and-insurance", "payments"));
        }
      }
      return;
    }

    setSubmitting(true);
    setLocalError(null);
    try {
      await axiosInstance.put(`/api/athena/digital-checkin/appointment/${encodeURIComponent(String(appointmentId))}/health-history-forms`, [], {
        headers: { client: 'axis', domain: 'axis' }
      });

      // Navigate to next step in sequence or completion
      if (isAllStepsCompleted()) {
        navigate(`/appointments/${appointmentId}/complete`);
      } else {
        const nextStep = getNextStepInSequence(location.pathname);
        if (nextStep) {
          navigate(nextStep.stepUrl);
        } else {
          navigate(carePath.replace("care-and-insurance", "payments"));
        }
      }
    } catch (err) {
      setLocalError("Unable to save your information. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

   const progressSteps = steps;

  // Check if all forms are complete
  const allFormsComplete = React.useMemo(() => {
    const sectionTypes = ['MEDICAL', 'SURGICAL', 'VACCINATION', 'SOCIAL', 'OBGYN', 'FAMILY', 'ALLERGY', 'MEDICATION'];
    const existingSections = sectionTypes.filter(type => sectionsByType[type] !== null);
    
    if (existingSections.length === 0) {
      return false; // No sections loaded yet
    }
    
    // Check if all existing sections are complete
    return existingSections.every(type => {
      const section = sectionsByType[type];
      return section && section.complete === true;
    });
  }, [sectionsByType]);

  return (
    <div className="min-h-screen bg-[#F7F8FD] flex flex-col">
      <div className="flex flex-row px-10 py-6 shadow justify-between bg-white items-center">
        <span className="text-[#01545D] font-semibold text-2xl">
          Axis Integrated Mental Health
        </span>
        <span className="text-[#01545D] font-medium text-xl">Patient Check-In</span>
      </div>
      <main className="flex-grow container mx-auto flex flex-col items-center px-4 py-10">
<h1 className="text-4xl font-semibold text-[#01545D] text-center mb-3">
           Medical History
         </h1>
         <div className="text-xl text-[#1C1C1C] text-center mb-6">
           Help us understand your medical background
         </div>
         {loading && (
           <div className="text-sm text-[#5D6A85] mb-2">Loading form content…</div>
         )}
         {isFormAlreadySubmitted && (
           <div className="w-full max-w-[1200px] mb-2">
             <Alert 
               type="info" 
               onClose={() => {
                 setIsFormAlreadySubmitted(false);
               }}
             >
               This health history form has already been submitted. You can continue to the next step.
             </Alert>
           </div>
         )}
         {(localError || workflowError) && !isFormAlreadySubmitted && (
           <div className="w-full max-w-[1200px] mb-2">
             <Alert 
               type="error" 
               onClose={() => {
                 setLocalError(null);
               }}
             >
               {workflowError || localError}
             </Alert>
           </div>
         )}
         <ProgressBar steps={progressSteps} />
        <form
          className="bg-white rounded-xl border border-[#A4BDC0] shadow-md p-10 w-full max-w-[1200px] flex flex-col gap-8 mt-4"
          onSubmit={handleSubmit}
        >
          {loading ? (
            <div className="text-center py-20">Loading...</div>
          ) : (
            <div className="flex flex-col gap-8">
              {allFormsComplete && (
                <div className="bg-[#E0F2F3] border border-[#01545D] rounded-lg px-6 py-5 flex items-center gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#01545D] flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="text-lg font-semibold text-[#01545D] mb-1">
                      All Forms Successfully Submitted
                    </div>
                    <div className="text-sm text-[#1C1C1C]">
                      All medical history forms have been completed and submitted successfully.
                    </div>
                  </div>
                </div>
              )}
              {/* MEDICAL Section */}
              {sectionsByType.MEDICAL && sectionsByType.MEDICAL.complete !== true && sectionsByType.MEDICAL.questions.length > 0 && (
                <section className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <div className="text-base font-semibold text-[#1C1C1C]">
                      Medical History
                    </div>
                    <div className="text-sm text-[#5D6A85]">
                      Select all that apply to your current or past medical history
                    </div>
                  </div>
                  <div className="border border-[#DCE1E8] rounded-xl bg-white p-4">
                    <div className="h-32 overflow-y-auto">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {sectionsByType.MEDICAL.questions.map((question) => {
                          const fieldsObj = Array.isArray(question.fields) ? {} : (question.fields || {});
                          const defaultValue = (fieldsObj as any)?.default || "N";
                          const isChecked = defaultValue === "Y";
                          return (
                            <label key={question.portalformquestionid} className="flex items-center gap-2 text-sm text-[#1C1C1C]">
                              <input
                                type="checkbox"
                                className="w-5 h-5 rounded border border-[#BEC5D2] accent-[#01545D]"
                                name={`medical_${question.portalformquestionid}`}
                                value="Y"
                                defaultChecked={isChecked}
                                onChange={() => markSectionTouched('MEDICAL')}
                              />
                              <span>{question.text}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {/* SURGICAL Section */}
              {sectionsByType.SURGICAL && sectionsByType.SURGICAL.complete !== true && sectionsByType.SURGICAL.questions.length > 0 && (
                <section className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <div className="text-base font-semibold text-[#1C1C1C]">
                      Surgical History
                    </div>
                    <div className="text-sm text-[#5D6A85]">
                      Select all that apply and provide surgery dates if available
                    </div>
                  </div>
                  <div className="border border-[#DCE1E8] rounded-xl bg-white p-4">
                    <div className="h-64 overflow-y-auto">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {sectionsByType.SURGICAL.questions.map((question) => {
                          const fieldsObj = Array.isArray(question.fields) ? {} : (question.fields || {});
                          const defaultValue = (fieldsObj as any)?.default || "N";
                          const isChecked = defaultValue === "Y";
                          const existingDate = (fieldsObj as any)?.surgerydate;
                          const surgeryDate = Array.isArray(existingDate) 
                            ? existingDate[0] 
                            : typeof existingDate === "string" 
                            ? existingDate 
                            : "";
                          const dateInputId = `surgical_${question.portalformquestionid}_date`;
                          const checkboxId = `surgical_${question.portalformquestionid}`;
                          
                          return (
                            <div key={question.portalformquestionid} className="flex flex-col gap-2 pb-3 border-b border-[#E2E8F0] last:border-b-0">
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  className="w-5 h-5 rounded border border-[#BEC5D2] accent-[#01545D]"
                                  name={`surgical_${question.portalformquestionid}_checked`}
                                  value="Y"
                                  defaultChecked={isChecked}
                                  id={checkboxId}
                                  onChange={(e) => {
                                    const dateInput = document.getElementById(dateInputId) as HTMLInputElement;
                                    const dateContainer = dateInput?.parentElement;
                                    if (dateContainer) {
                                      dateContainer.style.display = e.target.checked ? "flex" : "none";
                                    }
                                  }}
                                />
                                <label 
                                  htmlFor={checkboxId}
                                  className="text-sm text-[#1C1C1C] cursor-pointer"
                                >
                                  {question.text}
                                </label>
                              </div>
                              <div 
                                className="ml-7 flex flex-col gap-1"
                                style={{ display: isChecked ? "flex" : "none" }}
                              >
                                <label className="text-xs font-semibold text-[#5D6A85]">
                                  Surgery Date (Optional)
                                </label>
                                <input
                                  type="date"
                                  id={dateInputId}
                                  name={`surgical_${question.portalformquestionid}_date`}
                                  defaultValue={surgeryDate ? formatDateForInput(surgeryDate) : ""}
                                  onChange={() => markSectionTouched('SURGICAL')}
                                  className="w-full rounded-md border border-[#DCE1E8] py-2 px-3 text-sm text-[#1C1C1C] placeholder-[#818BA0] focus:border-[#01545D]"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {/* VACCINATION Section */}
              {sectionsByType.VACCINATION && sectionsByType.VACCINATION.complete !== true && sectionsByType.VACCINATION.questions.length > 0 && (
                <section className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <div className="text-base font-semibold text-[#1C1C1C]">
                      Immunizations
                    </div>
                    <div className="text-sm text-[#5D6A85]">
                      Select all that apply and provide administration dates (required when checked)
                    </div>
                  </div>
                  <div className="border border-[#DCE1E8] rounded-xl bg-white p-4">
                    <div className="h-64 overflow-y-auto">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {sectionsByType.VACCINATION.questions.map((question) => {
                          const fieldsObj = Array.isArray(question.fields) ? {} : (question.fields || {});
                          const defaultValue = (fieldsObj as any)?.default || "N";
                          const isChecked = defaultValue === "Y";
                          const existingDate = (fieldsObj as any)?.administered;
                          const administeredDate = Array.isArray(existingDate) 
                            ? existingDate[0] 
                            : typeof existingDate === "string" 
                            ? existingDate 
                            : "";
                          const dateInputId = `vaccination_${question.portalformquestionid}_date`;
                          const checkboxId = `vaccination_${question.portalformquestionid}`;
                          
                          return (
                            <div key={question.portalformquestionid} className="flex flex-col gap-2 pb-3 border-b border-[#E2E8F0] last:border-b-0">
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  className="w-5 h-5 rounded border border-[#BEC5D2] accent-[#01545D]"
                                  name={`vaccination_${question.portalformquestionid}_checked`}
                                  value="Y"
                                  defaultChecked={isChecked}
                                  id={checkboxId}
                                  onChange={(e) => {
                                    markSectionTouched('VACCINATION');
                                    const dateInput = document.getElementById(dateInputId) as HTMLInputElement;
                                    const dateContainer = dateInput?.parentElement;
                                    if (dateContainer) {
                                      dateContainer.style.display = e.target.checked ? "flex" : "none";
                                      if (e.target.checked) {
                                        dateInput?.setAttribute("required", "required");
                                      } else {
                                        dateInput?.removeAttribute("required");
                                      }
                                    }
                                  }}
                                />
                                <label 
                                  htmlFor={checkboxId}
                                  className="text-sm text-[#1C1C1C] cursor-pointer"
                                >
                                  {question.text}
                                </label>
                              </div>
                              <div 
                                className="ml-7 flex flex-col gap-1"
                                style={{ display: isChecked ? "flex" : "none" }}
                              >
                                <label className="text-xs font-semibold text-[#1C1C1C]">
                                  Administration Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="date"
                                  id={dateInputId}
                                  name={`vaccination_${question.portalformquestionid}_date`}
                                  defaultValue={administeredDate ? formatDateForInput(administeredDate) : ""}
                                  required={isChecked}
                                  onChange={() => markSectionTouched('VACCINATION')}
                                  className="w-full rounded-md border border-[#DCE1E8] py-2 px-3 text-sm text-[#1C1C1C] placeholder-[#818BA0] focus:border-[#01545D]"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {/* SOCIAL Section */}
              {sectionsByType.SOCIAL && sectionsByType.SOCIAL.complete !== true && sectionsByType.SOCIAL.questions.length > 0 && (
                <section className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <div className="text-base font-semibold text-[#1C1C1C]">
                      Social History
                    </div>
                    <div className="text-sm text-[#5D6A85]">
                      Please answer the following questions
                    </div>
                  </div>
                  <div className="border border-[#DCE1E8] rounded-xl bg-white p-4">
                    <div className="h-96 overflow-y-auto">
                      <div className="flex flex-col gap-6">
                        {[...sectionsByType.SOCIAL.questions]
                          .sort((a, b) => {
                            const orderA = a.ordering ? parseInt(a.ordering, 10) : 999;
                            const orderB = b.ordering ? parseInt(b.ordering, 10) : 999;
                            return orderA - orderB;
                          })
                          .map((question) => (
                            <div key={question.portalformquestionid} className="flex flex-col gap-2">
                              <label className="text-sm font-semibold text-[#1C1C1C]">
                                {question.text}
                              </label>
                              {renderCustomQuestion(question, "SOCIAL", () => markSectionTouched('SOCIAL'))}
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {/* OBGYN Section */}
              {sectionsByType.OBGYN && sectionsByType.OBGYN.complete !== true && sectionsByType.OBGYN.questions.length > 0 && (
                <section className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <div className="text-base font-semibold text-[#1C1C1C]">
                      OBGYN History
                    </div>
                    <div className="text-sm text-[#5D6A85]">
                      Please answer the following questions
                    </div>
                  </div>
                  <div className="border border-[#DCE1E8] rounded-xl bg-white p-4">
                    <div className="h-96 overflow-y-auto">
                      <div className="flex flex-col gap-6">
                        {[...sectionsByType.OBGYN.questions]
                          .sort((a, b) => {
                            const orderA = a.ordering ? parseInt(a.ordering, 10) : 999;
                            const orderB = b.ordering ? parseInt(b.ordering, 10) : 999;
                            return orderA - orderB;
                          })
                          .map((question) => (
                            <div key={question.portalformquestionid} className="flex flex-col gap-2">
                              <label className="text-sm font-semibold text-[#1C1C1C]">
                                {question.text}
                              </label>
                              {renderCustomQuestion(question, "OBGYN", () => markSectionTouched('OBGYN'))}
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {/* FAMILY Section */}
              {sectionsByType.FAMILY && sectionsByType.FAMILY.complete !== true && sectionsByType.FAMILY.questions.length > 0 && (
                <section className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <div className="text-base font-semibold text-[#1C1C1C]">
                      Family History
                    </div>
                    <div className="text-sm text-[#5D6A85]">
                      Select conditions and add family members who have them. You can add multiple instances per condition.
                    </div>
                  </div>
                  <div className="border border-[#DCE1E8] rounded-xl bg-white p-4">
                    <div className="h-96 overflow-y-auto">
                      <div className="flex flex-col gap-6">
                        {sectionsByType.FAMILY.questions.map((problem) => {
                        const problemId = problem.portalformquestionid;
                        const instanceIds = familyInstances[problemId] || [];
                        // For FAMILY sections, fields is directly an array of instances
                        const existingFields = Array.isArray(problem.fields) ? problem.fields : [];
                        const relationOptions = sectionsByType.FAMILY?.fielddata?.relation?.dropdownvalues || [];
                        
                        return (
                          <div key={problemId} className="flex flex-col gap-3 pb-4 border-b border-[#E2E8F0] last:border-b-0 last:pb-0">
                            <div className="flex items-center justify-between">
                              <div className="text-sm font-semibold text-[#1C1C1C]">
                                {problem.text}
                              </div>
                              <button
                                type="button"
                                onClick={() => addFamilyInstance(problemId)}
                                className="text-xs text-[#01545D] underline"
                              >
                                + Add Instance
                              </button>
                            </div>
                            
                            {instanceIds.length === 0 && (
                              <div className="text-sm text-[#5D6A85] italic">
                                No instances added yet. Click "+ Add Instance" to add a family member with this condition.
                              </div>
                            )}
                            
                            {instanceIds.map((instanceId, instanceIdx) => {
                              // Get existing data for this instance
                              const existingInstance = Array.isArray(existingFields) && existingFields[instanceIdx] 
                                ? existingFields[instanceIdx] 
                                : null;
                              const existingRelation = existingInstance?.relation || "";
                              const existingRelationKeyId = existingInstance?.relationkeyid || "";
                              const existingOnsetAge = existingInstance?.onsetage || "";
                              const existingDiedOfAge = existingInstance?.diedofage || "";
                              const existingDefault = existingInstance?.default || "N";
                              
                              // Use existing relationkeyid if available, otherwise leave empty (will be generated on submit)
                              const relationKeyId = existingRelationKeyId || "";
                              
                              return (
                                <div
                                  key={instanceId}
                                  className="border border-[#E2E8F0] rounded-lg bg-[#F7F8FD] p-4 flex flex-col gap-3"
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="text-xs font-semibold text-[#01545D]">
                                      Instance #{instanceIdx + 1}
                                    </div>
                                    <button
                                      type="button"
                                      className="text-xs text-[#FA4D5E]"
                                      onClick={() => removeFamilyInstance(problemId, instanceId)}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="flex flex-col gap-1">
                                      <label className="text-xs font-semibold text-[#1C1C1C]">
                                        Has this condition? <span className="text-red-500">*</span>
                                      </label>
                                      <div className="flex gap-4">
                                        <label className="flex items-center gap-2 text-sm text-[#1C1C1C]">
                                          <input
                                            type="radio"
                                            name={`family_${problemId}_${instanceId}_default`}
                                            value="Y"
                                            defaultChecked={existingDefault === "Y"}
                                            onChange={() => markSectionTouched('FAMILY')}
                                            className="w-4 h-4 border border-[#BEC5D2] accent-[#01545D]"
                                          />
                                          <span>Yes</span>
                                        </label>
                                        <label className="flex items-center gap-2 text-sm text-[#1C1C1C]">
                                          <input
                                            type="radio"
                                            name={`family_${problemId}_${instanceId}_default`}
                                            value="N"
                                            defaultChecked={existingDefault === "N" || !existingDefault}
                                            onChange={() => markSectionTouched('FAMILY')}
                                            className="w-4 h-4 border border-[#BEC5D2] accent-[#01545D]"
                                          />
                                          <span>No</span>
                                        </label>
                                      </div>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <label className="text-xs font-semibold text-[#1C1C1C]">
                                        Relationship <span className="text-red-500">*</span>
                                      </label>
                                      <select
                                        name={`family_${problemId}_${instanceId}_relation`}
                                        required
                                        onChange={() => markSectionTouched('FAMILY')}
                                        className="rounded-md border border-[#DCE1E8] py-2 px-3 text-sm text-[#1C1C1C] bg-white focus:border-[#01545D]"
                                        defaultValue={existingRelation}
                                      >
                                        <option value="">Select relationship</option>
                                        {relationOptions.map(([value, label]) => (
                                          <option key={value} value={value}>
                                            {label}
                                          </option>
                                        ))}
                                      </select>
                                      <input
                                        type="hidden"
                                        name={`family_${problemId}_${instanceId}_relationkeyid`}
                                        value={relationKeyId}
                                      />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <label className="text-xs font-semibold text-[#1C1C1C]">
                                        Onset Age (optional)
                                      </label>
                                      <input
                                        type="number"
                                        name={`family_${problemId}_${instanceId}_onsetage`}
                                        placeholder="Age when condition started"
                                        defaultValue={existingOnsetAge}
                                        onChange={() => markSectionTouched('FAMILY')}
                                        className="rounded-md border border-[#DCE1E8] py-2 px-3 text-sm text-[#1C1C1C] placeholder-[#818BA0] focus:border-[#01545D]"
                                      />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <label className="text-xs font-semibold text-[#1C1C1C]">
                                        Age at Death (optional)
                                      </label>
                                      <input
                                        type="number"
                                        name={`family_${problemId}_${instanceId}_diedofage`}
                                        placeholder="Age when died from this condition"
                                        defaultValue={existingDiedOfAge}
                                        onChange={() => markSectionTouched('FAMILY')}
                                        className="rounded-md border border-[#DCE1E8] py-2 px-3 text-sm text-[#1C1C1C] placeholder-[#818BA0] focus:border-[#01545D]"
                                      />
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {/* ALLERGY Section */}
              {sectionsByType.ALLERGY && sectionsByType.ALLERGY.complete !== true && (
                <section className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <div className="text-base font-semibold text-[#1C1C1C]">
                      Allergies
                    </div>
                    <div className="text-sm text-[#5D6A85]">
                      Search and add all of your allergies. You can add multiple reactions per allergy.
                    </div>
                  </div>
                  {Object.keys(allergies).length === 0 && (
                    <button
                      type="button"
                      onClick={addAllergy}
                      className="inline-flex items-center justify-center border-dashed border-[#01545D] rounded-md px-5 py-3 text-sm font-semibold text-[#01545D]"
                    >
                      + Add Allergy
                    </button>
                  )}
                  {Object.entries(allergies).map(([allergyIdStr, allergyData], index) => {
                    const allergyId = parseFloat(allergyIdStr);
                    const reactionIds = allergyReactions[allergyId] || [];
                    const searchResults = allergySearchResults[allergyId] || [];
                    const searchValue = allergySearchValue[allergyId] || "";
                    const isLoading = allergySearchLoading[allergyId] || false;
                    
                    return (
                      <div
                        key={allergyId}
                        className="border border-[#DCE1E8] rounded-xl bg-white p-4 flex flex-col gap-4"
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-semibold text-[#01545D]">
                            Allergy #{index + 1}
                          </div>
                          <button
                            type="button"
                            className="text-xs text-[#FA4D5E]"
                            onClick={() => removeAllergy(allergyId)}
                          >
                            Remove
                          </button>
                        </div>
                        
                        {/* Allergen Search */}
                        <div className="flex flex-col gap-1">
                          <label className="text-sm font-semibold text-[#1C1C1C]">
                            Allergen <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              placeholder="Search for allergen (e.g., Penicillin, Peanuts)"
                              value={searchValue}
                              onChange={(e) => handleAllergySearchChange(allergyId, e.target.value)}
                              className="w-full rounded-md border border-[#DCE1E8] py-3 px-3 text-sm text-[#1C1C1C] placeholder-[#818BA0] focus:border-[#01545D]"
                            />
                            {isLoading && (
                              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-[#5D6A85]">
                                Searching...
                              </div>
                            )}
                          </div>
                          
                          {/* Search Results */}
                          {searchResults.length > 0 && (
                            <div className="mt-1 border border-[#DCE1E8] rounded-md bg-white max-h-48 overflow-y-auto">
                              {searchResults.map((allergy) => (
                                <button
                                  key={allergy.allergenid}
                                  type="button"
                                  onClick={() => handleSelectAllergy(allergyId, allergy)}
                                  className="w-full text-left px-3 py-2 hover:bg-[#F7F8FD] text-sm text-[#1C1C1C] border-b border-[#E2E8F0] last:border-b-0"
                                >
                                  {allergy.allergenname || allergy.allergy || ""}
                                </button>
                              ))}
                            </div>
                          )}
                          
                          {/* Selected Allergy */}
                          {allergyData.name && (
                            <div className="mt-2 px-3 py-2 bg-[#F7F8FD] rounded-md text-sm text-[#1C1C1C]">
                              Selected: <span className="font-semibold">{allergyData.name}</span>
                            </div>
                          )}
                          
                          <input
                            type="hidden"
                            name={`allergy_${allergyId}_name`}
                            value={allergyData.name || ""}
                          />
                          <input
                            type="hidden"
                            name={`allergy_${allergyId}_allergenid`}
                            value={allergyData.allergenid || ""}
                          />
                        </div>
                        
                        {/* Onset Date */}
                        <div className="flex flex-col gap-1">
                          <label className="text-sm font-semibold text-[#1C1C1C]">
                            Onset Date (optional)
                          </label>
                          <input
                            type="date"
                            name={`allergy_${allergyId}_onsetdate`}
                            defaultValue={allergyData.onsetdate ? formatDateForInput(allergyData.onsetdate) : ""}
                            onChange={(e) => {
                              markSectionTouched('ALLERGY');
                              const formattedDate = formatDateForBackend(e.target.value);
                              setAllergies((prev) => ({
                                ...prev,
                                [allergyId]: {
                                  ...prev[allergyId],
                                  onsetdate: formattedDate,
                                },
                              }));
                            }}
                            className="w-full md:w-48 rounded-md border border-[#DCE1E8] py-3 px-3 text-sm text-[#1C1C1C] placeholder-[#818BA0] focus:border-[#01545D]"
                          />
                        </div>
                        
                        {/* Reactions & Severities */}
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-semibold text-[#1C1C1C]">
                              Reactions & Severity
                            </div>
                            <button
                              type="button"
                              onClick={() => addAllergyReaction(allergyId)}
                              className="text-xs text-[#01545D] underline"
                            >
                              + Add Reaction
                            </button>
                          </div>
                          
                          {reactionIds.length === 0 && (
                            <div className="text-sm text-[#5D6A85] italic">
                              No reactions added yet. Click "+ Add Reaction" to add one.
                            </div>
                          )}
                          
                          {reactionIds.map((reactionInstanceId, reactionIdx) => {
                            const existingReaction = allergyData.reactions[reactionIdx];
                            return (
                              <div
                                key={reactionInstanceId}
                                className="border border-[#E2E8F0] rounded-lg bg-[#F7F8FD] p-3 flex flex-col gap-3"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="text-xs font-semibold text-[#01545D]">
                                    Reaction #{reactionIdx + 1}
                                  </div>
                                  <button
                                    type="button"
                                    className="text-xs text-[#FA4D5E]"
                                    onClick={() => removeAllergyReaction(allergyId, reactionInstanceId)}
                                  >
                                    Remove
                                  </button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="flex flex-col gap-1">
                                    <label className="text-xs font-semibold text-[#1C1C1C]">
                                      Reaction <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                      name={`allergy_${allergyId}_reaction_${reactionInstanceId}_id`}
                                      required
                                      defaultValue={existingReaction?.reactionId || ""}
                                      onChange={() => markSectionTouched('ALLERGY')}
                                      className="rounded-md border border-[#DCE1E8] py-2 px-3 text-sm text-[#1C1C1C] bg-white focus:border-[#01545D]"
                                    >
                                      <option value="">Select Reaction</option>
                                      {(sectionsByType.ALLERGY?.fielddata?.allergyreactionid?.dropdownvalues || []).map((reaction) => {
                                        const [value, label] = reaction;
                                        return (
                                          <option key={value} value={value}>
                                            {label}
                                          </option>
                                        );
                                      })}
                                    </select>
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <label className="text-xs font-semibold text-[#1C1C1C]">
                                      Severity <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                      name={`allergy_${allergyId}_reaction_${reactionInstanceId}_severity`}
                                      required
                                      defaultValue={existingReaction?.severityId || ""}
                                      onChange={() => markSectionTouched('ALLERGY')}
                                      className="rounded-md border border-[#DCE1E8] py-2 px-3 text-sm text-[#1C1C1C] bg-white focus:border-[#01545D]"
                                    >
                                      <option value="">Select Severity</option>
                                      {(sectionsByType.ALLERGY?.fielddata?.severityid?.dropdownvalues || []).map((severity) => {
                                        const severityId = typeof severity === 'object' ? severity.id : severity;
                                        const severityLabel = typeof severity === 'object' ? severity.description : severity;
                                        return (
                                          <option key={severityId} value={severityId}>
                                            {severityLabel}
                                          </option>
                                        );
                                      })}
                                    </select>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  
                  {Object.keys(allergies).length > 0 && (
                    <button
                      type="button"
                      onClick={addAllergy}
                      className="inline-flex items-center justify-center border-dashed border-[#01545D] rounded-md px-5 py-3 text-sm font-semibold text-[#01545D]"
                    >
                      + Add Another Allergy
                    </button>
                  )}
                </section>
              )}

              {/* MEDICATION Section */}
              {sectionsByType.MEDICATION && sectionsByType.MEDICATION.complete !== true && (
                <section className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <div className="text-base font-semibold text-[#1C1C1C]">
                      Medications
                    </div>
                    <div className="text-sm text-[#5D6A85]">
                      Search and add all of your current and past medications.
                    </div>
                  </div>
                  {Object.keys(medications).length === 0 && (
                    <button
                      type="button"
                      onClick={addMedication}
                      className="inline-flex items-center justify-center border-dashed border-[#01545D] rounded-md px-5 py-3 text-sm font-semibold text-[#01545D]"
                    >
                      + Add Medication
                    </button>
                  )}
                  {Object.entries(medications).map(([medicationIdStr, medicationData], index) => {
                    const medicationId = parseFloat(medicationIdStr);
                    const searchResults = medicationSearchResults[medicationId] || [];
                    const searchValue = medicationSearchValue[medicationId] || "";
                    const isLoading = medicationSearchLoading[medicationId] || false;
                    const rxnormTypeOptions = (sectionsByType.MEDICATION?.fielddata as any)?.rxnormtype?.dropdownvalues || [];
                    const deactivateReasonOptions = (sectionsByType.MEDICATION?.fielddata as any)?.meddeactivatereasonid?.dropdownvalues || [];
                    // Check if stopdate exists (could be in MM/DD/YYYY or YYYY-MM-DD format)
                    const hasStopDate = medicationData.stopdate ? true : false;
                    
                    return (
                      <div
                        key={medicationId}
                        className="border border-[#DCE1E8] rounded-xl bg-white p-4 flex flex-col gap-4"
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-semibold text-[#01545D]">
                            Medication #{index + 1}
                          </div>
                          <button
                            type="button"
                            className="text-xs text-[#FA4D5E]"
                            onClick={() => removeMedication(medicationId)}
                          >
                            Remove
                          </button>
                        </div>
                        
                        {/* Medication Search */}
                        <div className="flex flex-col gap-1">
                          <label className="text-sm font-semibold text-[#1C1C1C]">
                            Medication <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              placeholder="Search for medication (e.g., Ibuprofen, Sertraline)"
                              value={searchValue}
                              onChange={(e) => handleMedicationSearchChange(medicationId, e.target.value)}
                              className="w-full rounded-md border border-[#DCE1E8] py-3 px-3 text-sm text-[#1C1C1C] placeholder-[#818BA0] focus:border-[#01545D]"
                            />
                            {isLoading && (
                              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-[#5D6A85]">
                                Searching...
                              </div>
                            )}
                          </div>
                          
                          {/* Search Results */}
                          {searchResults.length > 0 && (
                            <div className="mt-1 border border-[#DCE1E8] rounded-md bg-white max-h-48 overflow-y-auto">
                              {searchResults.map((medication) => (
                                <button
                                  key={medication.medicationid}
                                  type="button"
                                  onClick={() => handleSelectMedication(medicationId, medication)}
                                  className="w-full text-left px-3 py-2 hover:bg-[#F7F8FD] text-sm text-[#1C1C1C] border-b border-[#E2E8F0] last:border-b-0"
                                >
                                  {medication.medication}
                                </button>
                              ))}
                            </div>
                          )}
                          
                          {/* Selected Medication */}
                          {medicationData.name && (
                            <div className="mt-2 px-3 py-2 bg-[#F7F8FD] rounded-md text-sm text-[#1C1C1C]">
                              Selected: <span className="font-semibold">{medicationData.name}</span>
                            </div>
                          )}
                          
                          <input
                            type="hidden"
                            name={`medication_${medicationId}_medicationid`}
                            value={medicationData.medicationid || ""}
                          />
                          <input
                            type="hidden"
                            name={`medication_${medicationId}_name`}
                            value={medicationData.name || ""}
                          />
                          <input
                            type="hidden"
                            name={`medication_${medicationId}_rxnormid`}
                            value={medicationData.rxnormid || ""}
                          />
                          <input
                            type="hidden"
                            name={`medication_${medicationId}_rxnormtype`}
                            value={medicationData.rxnormtype || ""}
                          />
                          <input
                            type="hidden"
                            name={`medication_${medicationId}_rxnormname`}
                            value={medicationData.rxnormname || ""}
                          />
                        </div>
                        
                        {/* RxNorm Type (if available) */}
                        {rxnormTypeOptions.length > 0 && medicationData.rxnormtype && (
                          <div className="flex flex-col gap-1">
                            <label className="text-sm font-semibold text-[#1C1C1C]">
                              RxNorm Type
                            </label>
                            <select
                              name={`medication_${medicationId}_rxnormtype`}
                              defaultValue={medicationData.rxnormtype || ""}
                              onChange={() => markSectionTouched('MEDICATION')}
                              className="w-full md:w-48 rounded-md border border-[#DCE1E8] py-3 px-3 text-sm text-[#1C1C1C] bg-white focus:border-[#01545D]"
                            >
                              <option value="">Select RxNorm Type</option>
                              {rxnormTypeOptions.map(([value, label]: [string, string]) => (
                                <option key={value} value={value}>
                                  {label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        
                        {/* Start Date, Stop Date, and Reason for Stopping - Side by Side */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* Start Date */}
                          <div className="flex flex-col gap-1">
                            <label className="text-sm font-semibold text-[#1C1C1C]">
                              Start Date (optional)
                            </label>
                            <input
                              type="date"
                              name={`medication_${medicationId}_startdate`}
                              defaultValue={medicationData.startdate ? formatDateForInput(medicationData.startdate) : ""}
                              onChange={(e) => {
                                markSectionTouched('MEDICATION');
                                const formattedDate = formatDateForBackend(e.target.value);
                                setMedications((prev) => ({
                                  ...prev,
                                  [medicationId]: {
                                    ...prev[medicationId],
                                    startdate: formattedDate,
                                  },
                                }));
                              }}
                              className="w-full rounded-md border border-[#DCE1E8] py-3 px-3 text-sm text-[#1C1C1C] placeholder-[#818BA0] focus:border-[#01545D]"
                            />
                          </div>
                          
                          {/* Stop Date */}
                          <div className="flex flex-col gap-1">
                            <label className="text-sm font-semibold text-[#1C1C1C]">
                              Stop Date (optional)
                            </label>
                            <input
                              type="date"
                              name={`medication_${medicationId}_stopdate`}
                              defaultValue={medicationData.stopdate ? formatDateForInput(medicationData.stopdate) : ""}
                              onChange={(e) => {
                                markSectionTouched('MEDICATION');
                                const formattedDate = formatDateForBackend(e.target.value);
                                setMedications((prev) => ({
                                  ...prev,
                                  [medicationId]: {
                                    ...prev[medicationId],
                                    stopdate: formattedDate || undefined,
                                  },
                                }));
                              }}
                              className="w-full rounded-md border border-[#DCE1E8] py-3 px-3 text-sm text-[#1C1C1C] placeholder-[#818BA0] focus:border-[#01545D]"
                            />
                          </div>
                          
                          {/* Deactivation Reason (only if stopdate is filled) */}
                          <div className="flex flex-col gap-1">
                            <label className="text-sm font-semibold text-[#1C1C1C]">
                              Reason for Stopping (optional)
                            </label>
                            <select
                              name={`medication_${medicationId}_meddeactivatedreasonid`}
                              defaultValue={medicationData.meddeactivatedreasonid || ""}
                              disabled={!hasStopDate}
                              onChange={() => markSectionTouched('MEDICATION')}
                              className="w-full rounded-md border border-[#DCE1E8] py-3 px-3 text-sm text-[#1C1C1C] bg-white focus:border-[#01545D] disabled:bg-gray-100 disabled:cursor-not-allowed"
                            >
                              <option value="">Select Reason</option>
                              {deactivateReasonOptions.map(([value, label]: [string, string]) => (
                                <option key={value} value={value}>
                                  {label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        
                        {/* External Note (read-only, if available) */}
                        {medicationData.externalnote && (
                          <div className="flex flex-col gap-1">
                            <label className="text-sm font-semibold text-[#1C1C1C]">
                              External Note (read-only)
                            </label>
                            <div className="px-3 py-2 bg-[#F7F8FD] rounded-md text-sm text-[#5D6A85]">
                              {medicationData.externalnote}
                            </div>
                            <input
                              type="hidden"
                              name={`medication_${medicationId}_externalnote`}
                              value={medicationData.externalnote}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {Object.keys(medications).length > 0 && (
                    <button
                      type="button"
                      onClick={addMedication}
                      className="inline-flex items-center justify-center border-dashed border-[#01545D] rounded-md px-5 py-3 text-sm font-semibold text-[#01545D]"
                    >
                      + Add Another Medication
                    </button>
                  )}
                </section>
              )}

              <div className="flex gap-5 w-full mt-6">
                <AppButton
                  variant="secondary"
                  type="button"
                  onClick={() => {
                    const previousStep = getPreviousStep(location.pathname);
                    if (previousStep) {
                      navigate(previousStep.stepUrl);
                    } else {
                      navigate(carePath);
                    }
                  }}
                  disabled={!!workflowError || submitting}
                  className="flex-1"
                >
                  Back
                </AppButton>
                {/* <AppButton
                  variant="secondary"
                  type="button"
                  onClick={handleSaveAndExit}
                  disabled={submitting}
                  className="flex-1"
                >
                  {submitting ? "Saving..." : "Save & Exit"}
                </AppButton> */}
                <AppButton type="submit" disabled={!!workflowError || submitting} className="flex-1">
                  {submitting ? "Saving..." : "Continue"}
                </AppButton>
              </div>
            </div>
            )}
        </form>
      </main>
    </div>
  );
};

export default MedicalHistory;
